/**
 * R14-A3 — Host Approval Tray.
 *
 * Listens to LiveKit data messages of type `host_approval_card` from the
 * backend BotRunner and renders pending escalation cards. Visible only to
 * staff (keyholder + cohosts). Auto-clears after 10 minutes of no decision.
 *
 * Props:
 *   - isStaff      (boolean) — whether the local participant is staff
 *   - cards        (array)   — pending cards from the parent
 *   - onDecision   ({card_id, decision}) => void
 *
 * Testids:
 *   - host-approval-tray
 *   - host-approval-card-{id}
 *   - host-approval-allow-{id}
 *   - host-approval-deny-{id}
 *   - host-approval-snooze-{id}
 */
import React, { useMemo } from "react";
import { Shield, Check, X, Clock, Camera, MessageSquareWarning, AlertOctagon } from "lucide-react";


export default function HostApprovalTray({ isStaff, cards, onDecision }) {
  const visible = useMemo(() => isStaff && Array.isArray(cards) && cards.length > 0, [isStaff, cards]);
  if (!visible) {
    // Still render an empty container with the testid so the test suite can find it.
    return <div data-testid="host-approval-tray" className="hidden" />;
  }

  return (
    <div
      className="fixed top-20 right-4 z-40 w-[340px] max-w-[88vw] flex flex-col gap-2"
      data-testid="host-approval-tray"
      aria-label="Host approval tray"
    >
      <div className="px-3 py-1.5 flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-amber-300/85 bg-amber-500/8 border border-amber-500/30 rounded-md backdrop-blur">
        <Shield className="w-3.5 h-3.5" />
        <span className="font-bold">Bot escalations</span>
        <span className="ml-auto opacity-65">{cards.length}</span>
      </div>
      {cards.map((c) => (
        <ApprovalCard key={c.card_id} card={c} onDecision={onDecision} />
      ))}
    </div>
  );
}


function ApprovalCard({ card, onDecision }) {
  const botIcon = card.bot === "camera_bot" ? Camera : MessageSquareWarning;
  const IconC = botIcon;
  return (
    <div
      className="rounded-lg border border-amber-400/30 bg-[#1a120c]/95 backdrop-blur p-3 shadow-[0_8px_28px_rgba(0,0,0,0.55)]"
      data-testid={`host-approval-card-${card.card_id}`}
    >
      <div className="flex items-start gap-2 mb-2">
        <div className="w-7 h-7 rounded grid place-items-center bg-amber-500/15 text-amber-300 shrink-0">
          <IconC className="w-3.5 h-3.5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.16em] text-amber-300/85 font-bold">
            {(card.bot || "bot").replace("_", " ")}
          </div>
          <div className="text-sm font-semibold mt-0.5 truncate">
            {card.kind === "removal" ? "Remove" : "Review"} {card.target_display_name || card.target_identity}?
          </div>
        </div>
      </div>
      <p className="text-xs text-white/75 leading-snug mb-2.5">{card.reason}</p>
      {card.evidence?.cam_state && (
        <div className="text-[10px] text-white/45 mb-2.5">
          Evidence: cam state <span className="font-mono text-white/65">{card.evidence.cam_state}</span>
          {card.evidence.since && (
            <span> · since {new Date((card.evidence.since || 0) * 1000).toLocaleTimeString()}</span>
          )}
        </div>
      )}
      <div className="flex items-center gap-1.5">
        <button
          onClick={() => onDecision({ card_id: card.card_id, decision: "allow" })}
          className="flex-1 inline-flex items-center justify-center gap-1 px-2.5 py-1.5 rounded text-[10px] font-bold uppercase tracking-[0.14em] bg-rose-500/90 hover:bg-rose-500 text-white transition"
          data-testid={`host-approval-allow-${card.card_id}`}
        >
          <Check className="w-3 h-3" /> Allow
        </button>
        <button
          onClick={() => onDecision({ card_id: card.card_id, decision: "deny" })}
          className="flex-1 inline-flex items-center justify-center gap-1 px-2.5 py-1.5 rounded text-[10px] font-bold uppercase tracking-[0.14em] bg-white/10 hover:bg-white/15 text-white/85 transition"
          data-testid={`host-approval-deny-${card.card_id}`}
        >
          <X className="w-3 h-3" /> Deny
        </button>
        <button
          onClick={() => onDecision({ card_id: card.card_id, decision: "snooze" })}
          className="inline-flex items-center justify-center gap-1 px-2.5 py-1.5 rounded text-[10px] font-bold uppercase tracking-[0.14em] bg-white/5 hover:bg-white/10 text-white/55 transition"
          title="Snooze for 5 minutes"
          data-testid={`host-approval-snooze-${card.card_id}`}
        >
          <Clock className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}
